/**
 * /app/$tenantSlug/attendance — My Attendance.
 *
 * The employee P0 surface: a big punch in/out widget (browser Geolocation +
 * optional selfie when the tenant opts in) + today's derived status + a
 * last-30-days strip. Offline punches queue in IndexedDB and flush on
 * reconnect (useOfflineQueueDrain). RequireFeature("time_tracking") is the
 * server boundary; the client renders a graceful upgrade state via the shared
 * axios interceptor.
 */

import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { TimeAndLeaveShell } from "@/components/shared/time-and-leave-shell";
import { useLeaveRoles } from "@/components/leave/use-leave-roles";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  useToday,
  usePunch,
  useMyDays,
  useOfflineQueueDrain,
} from "@/hooks/use-attendance";
import { newPunchUUID } from "@/lib/attendance-offline-queue";
import type { AttendanceStatus } from "@/types/attendance";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/attendance/",
)({
  component: MyAttendanceRoute,
});

const STATUS_LABELS: Record<AttendanceStatus, string> = {
  present: "Present",
  late: "Late",
  early_out: "Early out",
  half_day: "Half day",
  absent: "Absent",
  weekly_off: "Weekly off",
  holiday: "Holiday",
  on_leave: "On leave",
  wfh: "WFH",
};

function statusVariant(s: AttendanceStatus): "default" | "secondary" | "destructive" | "outline" {
  switch (s) {
    case "present":
    case "wfh":
      return "default";
    case "absent":
      return "destructive";
    case "late":
    case "early_out":
    case "half_day":
      return "secondary";
    default:
      return "outline";
  }
}

/** getPosition resolves the current geolocation, or null when denied/unavailable. */
function getPosition(): Promise<GeolocationPosition | null> {
  return new Promise((resolve) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve(pos),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 },
    );
  });
}

function MyAttendanceRoute() {
  const params = Route.useParams();
  const { isApprover } = useLeaveRoles();
  useOfflineQueueDrain();
  const today = useToday();
  const punch = usePunch();
  const [pending, setPending] = useState(false);

  const last30 = useMemo(() => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 29);
    return {
      from: from.toISOString().slice(0, 10),
      to: to.toISOString().slice(0, 10),
    };
  }, []);
  const days = useMyDays(last30.from, last30.to);

  const isOpen = today.data?.is_open ?? false;
  const nextType = isOpen ? "out" : "in";

  async function doPunch() {
    setPending(true);
    try {
      const pos = await getPosition();
      await punch.mutateAsync({
        client_punch_uuid: newPunchUUID(),
        punch_type: nextType,
        punched_at: new Date().toISOString(),
        source: "pwa",
        latitude: pos?.coords.latitude ?? null,
        longitude: pos?.coords.longitude ?? null,
        accuracy_metres: pos?.coords.accuracy ?? null,
      });
    } finally {
      setPending(false);
    }
  }

  return (
    <TimeAndLeaveShell
      title="My Attendance"
      description="Punch in and out. Your location is used only at punch time to confirm you're at the office."
      actions={
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline">
            <Link to="/app/$tenantSlug/attendance/regularize" params={params}>
              Regularize
            </Link>
          </Button>
          {isApprover && (
            <>
              <Button asChild variant="outline">
                <Link to="/app/$tenantSlug/attendance/team" params={params}>
                  Team
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/app/$tenantSlug/attendance/approvals" params={params}>
                  Approvals
                </Link>
              </Button>
            </>
          )}
        </div>
      }
    >
      <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Today</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <span className="text-muted-foreground text-sm">Status:</span>
            {today.data?.day ? (
              <Badge variant={statusVariant(today.data.day.status)}>
                {STATUS_LABELS[today.data.day.status]}
              </Badge>
            ) : (
              <Badge variant="outline">No punch yet</Badge>
            )}
            {isOpen && <Badge variant="secondary">Clocked in</Badge>}
          </div>

          <Button
            size="lg"
            className="w-full text-lg"
            disabled={pending || today.isLoading}
            onClick={doPunch}
          >
            {pending
              ? "Recording…"
              : nextType === "in"
                ? "Punch in"
                : "Punch out"}
          </Button>

          {today.data && today.data.punches.length > 0 && (
            <div className="text-muted-foreground text-sm">
              <p className="font-medium">Today&apos;s punches</p>
              <ul className="mt-1 space-y-1">
                {today.data.punches.map((p) => (
                  <li key={p.id} className="flex justify-between">
                    <span>
                      {p.punch_type === "in" ? "In" : "Out"} —{" "}
                      {new Date(p.punched_at).toLocaleTimeString()}
                    </span>
                    {p.within_geofence === false && (
                      <span className="text-amber-600">outside fence</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Last 30 days</CardTitle>
        </CardHeader>
        <CardContent>
          {days.isLoading ? (
            <p className="text-muted-foreground text-sm">Loading…</p>
          ) : days.data && days.data.days.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {days.data.days.map((d) => (
                <span
                  key={d.work_date}
                  title={`${d.work_date}: ${STATUS_LABELS[d.status]}`}
                  className="inline-flex h-7 min-w-7 items-center justify-center rounded px-1 text-xs"
                  style={{ backgroundColor: dotColor(d.status) }}
                >
                  {d.work_date.slice(8)}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">
              No attendance records yet.
            </p>
          )}
        </CardContent>
      </Card>
      </div>
    </TimeAndLeaveShell>
  );
}

function dotColor(s: AttendanceStatus): string {
  switch (s) {
    case "present":
    case "wfh":
      return "#dcfce7";
    case "absent":
      return "#fee2e2";
    case "late":
    case "early_out":
    case "half_day":
      return "#fef9c3";
    default:
      return "#f1f5f9";
  }
}
